import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import ErrorBoundary from "./components/error/ErrorBoundary";
import ConsentModal from "./components/consent/ConsentModal";
import api from "./lib/api";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/auth/LoginPage";
import RegisterPage from "./pages/auth/RegisterPage";
import OnboardingPage from "./pages/onboarding/OnboardingPage";
import DashboardPage from "./pages/dashboard/DashboardPage";
import EntitiesPage from "./pages/entities/EntitiesPage";
import EntityFormPage from "./pages/entities/EntityFormPage";
import EntityDetailPage from "./pages/entities/EntityDetailPage";
import AIAnalysisPage from "./pages/ai/AIAnalysisPage";
import AuditLogPage from "./pages/audit/AuditLogPage";
import AuditCertificationPage from "./pages/audit/AuditCertificationPage";
import RuleBuilderPage from "./pages/rules/RuleBuilderPage";
import StructureVisualizationPage from "./pages/structure/StructureVisualizationPage";
import VerifyCertificatePage from "./pages/verify/VerifyCertificatePage";
import PaymentsPage from "./pages/payments/PaymentsPage";
import PaymentSuccessPage from "./pages/payments/PaymentSuccessPage";
import TeamsPage from "./pages/teams/TeamsPage";
import DocuSignOAuthSuccessPage from "./pages/docusign/DocuSignOAuthSuccessPage";
import UCCPage from "./pages/ucc/UCCPage";
import ComplianceDashboardPage from "./pages/compliance/ComplianceDashboardPage";
import DeveloperPortalPage from "./pages/developer/DeveloperPortalPage";
import BillingPage from "./pages/billing/BillingPage";
import APIDocsPage from "./pages/api-docs/APIDocsPage";
import FundabilityDashboard from "./pages/intelligence/FundabilityDashboard";
import TrustCenter from "./pages/trust/TrustCenter";
import IPRegistryPage from "./pages/ip/IPRegistryPage";
import InvestorDiligencePage from "./pages/investor/InvestorDiligencePage";
import PricingPage from "./pages/pricing/PricingPage";
import CheckoutPage from "./pages/checkout/CheckoutPage";
import SigningPage from "./pages/signing/SigningPage";
import TrademarkFilingPage from "./pages/trademark/TrademarkFilingPage";
import CartPage from "./pages/cart/CartPage";
import FormationWizardPage from "./pages/formation/FormationWizardPage";
import VerifyIdentityPage from "./pages/verify/VerifyIdentityPage";
import ApiKeysPage from "./pages/settings/ApiKeysPage";
import WalletSettingsPage from "./pages/settings/WalletSettingsPage";
import { CartProvider } from "./context/CartContext";

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Public Route Component (redirect if logged in)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    if (!user.onboarding_completed) {
      return <Navigate to="/onboarding" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentStatus, setConsentStatus] = useState(null);

  // Check consent status when user logs in
  useEffect(() => {
    if (user) {
      checkConsentStatus();
    }
  }, [user]);

  const checkConsentStatus = async () => {
    try {
      const response = await api.get('/consent/status');
      setConsentStatus(response.data);
      
      // Show modal if any required consent is missing
      if (response.data.requires_acceptance) {
        const hasAnyMissing = Object.values(response.data.requires_acceptance).some(v => v === true);
        setShowConsentModal(hasAnyMissing);
      }
    } catch (error) {
      console.error('Failed to check consent status:', error);
    }
  };

  const handleConsentsAccepted = () => {
    setShowConsentModal(false);
    checkConsentStatus(); // Refresh status
  };

  return (
    <>
      <ConsentModal 
        isOpen={showConsentModal} 
        onClose={() => setShowConsentModal(false)}
        onConsentsAccepted={handleConsentsAccepted}
      />
      <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/verify/:certificateId?" element={<VerifyCertificatePage />} />
      <Route path="/sign/:token" element={<SigningPage />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />

      {/* Onboarding */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <OnboardingPage />
          </ProtectedRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/entities"
        element={
          <ProtectedRoute>
            <EntitiesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/entities/new"
        element={
          <ProtectedRoute>
            <EntityFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/entities/:id"
        element={
          <ProtectedRoute>
            <EntityDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/entities/:id/edit"
        element={
          <ProtectedRoute>
            <EntityFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-analysis"
        element={
          <ProtectedRoute>
            <AIAnalysisPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/audit-log"
        element={
          <ProtectedRoute>
            <AuditLogPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/audit-certification"
        element={
          <ProtectedRoute>
            <AuditCertificationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rule-builder"
        element={
          <ProtectedRoute>
            <RuleBuilderPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/structure"
        element={
          <ProtectedRoute>
            <StructureVisualizationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payments"
        element={
          <ProtectedRoute>
            <PaymentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams"
        element={
          <ProtectedRoute>
            <TeamsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ucc"
        element={
          <ProtectedRoute>
            <UCCPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/compliance"
        element={
          <ProtectedRoute>
            <ComplianceDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/developer"
        element={<Navigate to="/pricing" replace />}
      />
      <Route
        path="/billing"
        element={<Navigate to="/pricing" replace />}
      />
      <Route
        path="/documentation"
        element={<Navigate to="/trust-center" replace />}
      />
      <Route
        path="/pricing"
        element={
          <ProtectedRoute>
            <PricingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/checkout/:serviceId"
        element={
          <ProtectedRoute>
            <CheckoutPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cart"
        element={
          <ProtectedRoute>
            <CartPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/formation"
        element={
          <ProtectedRoute>
            <FormationWizardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/verify-identity"
        element={
          <ProtectedRoute>
            <VerifyIdentityPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/api-keys"
        element={
          <ProtectedRoute>
            <ApiKeysPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/wallets"
        element={
          <ProtectedRoute>
            <WalletSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/fundability"
        element={
          <ProtectedRoute>
            <FundabilityDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trust-center"
        element={
          <ProtectedRoute>
            <TrustCenter />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ip-registry"
        element={
          <ProtectedRoute>
            <IPRegistryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/investor-diligence"
        element={
          <ProtectedRoute>
            <InvestorDiligencePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trademark"
        element={
          <ProtectedRoute>
            <TrademarkFilingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payments/success"
        element={
          <ProtectedRoute>
            <PaymentSuccessPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/docusign/oauth/success"
        element={
          <ProtectedRoute>
            <DocuSignOAuthSuccessPage />
          </ProtectedRoute>
        }
      />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster position="top-right" />
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
